import React, { useState } from 'react';
import { message } from 'antd';
import copyToClipboard from 'copy-to-clipboard';
import { getStoreDetailsURL, updateStore, useStoreDetails } from 'api/store';
import { ownerSetSharedFirstStore } from 'api/owner_sdk';
import { mutate } from 'swr';
import { Button, InputHelperText, Modal, Spinner, Tabs, Typography } from 'ui-kit';
import './ShareModal.scss';
import LogoLinks from './LogoLinks';
import { getStoreLink, getIframeSnippet } from 'utils/utilsV2';
import TextArea from 'ui-kit/TextArea/TextArea';

interface IStoreLinkContentProps {
  storeLink: string;
  storeId: number;
  storeHasSharedStore: boolean;
}

const StoreLinkContent = ({ storeLink, storeId, storeHasSharedStore }: IStoreLinkContentProps) => {
  const handleCopyLink = () => {
    if (!storeHasSharedStore && storeId) {
      updateStore(storeId, { hasSharedStore: true }).then(() => {
        mutate(getStoreDetailsURL(storeId));
        window.fbq?.('trackCustom', 'ShareLink', { storeId });
      });
    }

    ownerSetSharedFirstStore();
    copyToClipboard(storeLink);
    message.success('Hooray! Happy guests await!');
  };

  return (
    <div style={{ width: '100%', marginBottom: '20px' }}>
      <Typography color="primary" variant="h6" className="ShareModal-links-header">
        Share Your Store
      </Typography>
      <InputHelperText>Share your store link in your automated guest messages:</InputHelperText>

      <p className="ShareModal-copyMessage">{storeLink}</p>
      <div className="ShareModal-messageCopyContainer">
        <Button variant="contained" onClick={handleCopyLink}>
          Copy Link
        </Button>
      </div>
    </div>
  );
};

interface IFrameStoreSnippetProps {
  storeIframe: string;
  storeId: number;
  storeHasSharedStore: boolean;
}

const StoreIframeContent = ({
  storeIframe,
  storeId,
  storeHasSharedStore,
}: IFrameStoreSnippetProps) => {
  const handleCopyLink = () => {
    if (!storeHasSharedStore && storeId) {
      updateStore(storeId, { hasSharedStore: true }).then(() => {
        mutate(getStoreDetailsURL(storeId));
        window.fbq?.('trackCustom', 'ShareLink', { storeId });
        ownerSetSharedFirstStore();
      });
    }

    ownerSetSharedFirstStore();
    copyToClipboard(storeIframe);
    message.success('Hooray! Happy guests await!');
  };

  return (
    <div style={{ width: '100%', marginBottom: '20px' }}>
      <Typography color="primary" variant="h6" className="ShareModal-links-header">
        Store iframe
      </Typography>
      <InputHelperText>
        Use this code to embed The Host Co on your website. For more information on how to use this
        code, contact your website developer. To share your store link, click the &quot;share
        store&quot; tab above.
      </InputHelperText>
      <p className="ShareModal-copyMessage">{storeIframe}</p>
      <div className="ShareModal-messageCopyContainer">
        <Button variant="contained" onClick={handleCopyLink}>
          Copy Code
        </Button>
      </div>
    </div>
  );
};

interface IMessageContent {
  storeLink: string;
  storeId: number;
  storeHasSharedStore: boolean;
}

const MessageContent = ({ storeLink, storeId, storeHasSharedStore }: IMessageContent) => {
  const [atBookingMessage, setAtBookingMessage] = useState(
    `Thanks for booking! We'll be in touch with the check-in specifics prior to your arrival. If you need driving directions or local recommendations, check out our Welcome Guide in the link below. You can also use this link to book an early check-in, late checkout, or a local service like massage or private chef. Please reach out if you have any other questions. \n\n${storeLink}`,
  );
  const [beforeCheckInMessage, setBeforeCheckInMessage] = useState(
    `Your trip is coming up! If you'd like to add any pre-order services like early check-in, a local massage or a tour guide, click the link below. These services require a few days advance notice so we recommend ordering them now if you're interested. Otherwise, we'll be in touch with entry instructions closer to your stay!\n\n${storeLink}`,
  );
  const [afterCheckInMessage, setAfterCheckInMessage] = useState(
    `Your trip is almost here! Look out for check-in instructions headed your way. For additional upgrades like extra guest, pet requests, late-checkout or local services click the link below. Safe travels!\n\n${storeLink}`,
  );

  const handleCopyLink = (copyId: string) => {
    let copyText = '';

    switch (copyId) {
      case 'at-booking':
        copyText = atBookingMessage;
        break;
      case 'before-check-in':
        copyText = beforeCheckInMessage;
        break;
      case 'after-check-in':
        copyText = afterCheckInMessage;
        break;
      default:
        copyText = '';
        break;
    }

    if (!storeHasSharedStore && storeId) {
      updateStore(storeId, { hasSharedStore: true }).then(() => {
        mutate(getStoreDetailsURL(storeId));
        window.fbq?.('trackCustom', 'ShareLink', { storeId });
      });
    }

    ownerSetSharedFirstStore();
    copyToClipboard(copyText);
    message.success('Hooray! Happy guests await!');
  };

  return (
    <div style={{ paddingTop: '20px' }}>
      <Typography color="primary" variant="h6" className="ShareModal-links-header">
        What to say to guests and when
      </Typography>
      <InputHelperText>
        You can edit these messages right in the box. Mention specific things guests can
        request—like late checkouts or local massages—since just saying you have a ‘store’ may not
        get clicks
      </InputHelperText>
      <p className="ShareModal-messageTitle">At booking:</p>
      <TextArea
        id="at-booking"
        rows={8}
        value={atBookingMessage}
        onChange={(e) => setAtBookingMessage(e.target.value)}
      />
      <div className="ShareModal-messageCopyContainer" style={{ marginTop: '10px' }}>
        <Button variant="contained" onClick={() => handleCopyLink('at-booking')}>
          Copy message
        </Button>
      </div>

      <p className="ShareModal-messageTitle">One week before check-in:</p>
      <TextArea
        id="before-check-in"
        rows={7}
        value={beforeCheckInMessage}
        onChange={(e) => setBeforeCheckInMessage(e.target.value)}
      />
      <div className="ShareModal-messageCopyContainer" style={{ marginTop: '10px' }}>
        <Button variant="contained" onClick={() => handleCopyLink('before-check-in')}>
          Copy message
        </Button>
      </div>

      <p className="ShareModal-messageTitle">At check-in:</p>
      <TextArea
        id="after-check-in"
        rows={5}
        value={afterCheckInMessage}
        onChange={(e) => setAfterCheckInMessage(e.target.value)}
      />
      <div className="ShareModal-messageCopyContainer" style={{ marginTop: '10px' }}>
        <Button variant="contained" onClick={() => handleCopyLink('after-check-in')}>
          Copy message
        </Button>
      </div>

      <p className="ShareModal-messageTitle">
        Additional templates for on-site product sales, rentals, guidebooks and more can be found
        here:{' '}
        <a
          target="_blank"
          rel="noreferrer"
          href="https://www.thehost.co/images-and-guest-messaging"
        >
          https://www.thehost.co/images-and-guest-messaging
        </a>
      </p>
    </div>
  );
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  storeId?: number;
}

const ShareModal = ({ isOpen, onClose, storeId: guestStoreId }: Props) => {
  const { data, isLoading } = useStoreDetails(guestStoreId, isOpen);
  const { id: storeId, hasClosedShareTutorial, hasSharedStore, enterpriseInfo } = data ?? {};

  const handleCloseShareTutorial = () => {
    updateStore(storeId, { hasClosedShareTutorial: true }).then(() => {
      mutate(getStoreDetailsURL(storeId));
    });
  };

  const { isEnabled, subdomain, isSubdomainEnabled } = enterpriseInfo ?? {};
  const [activeTab, setActiveTab] = useState('store-link');
  const storeLink = getStoreLink({
    storeId: storeId ?? guestStoreId,
    isEnabled,
    subdomain,
    isSubdomainEnabled,
  });
  const storeIframe = getIframeSnippet({ storeLink });

  return (
    <Modal
      className="ShareModal"
      visible={isOpen}
      onCancel={onClose}
      closable={hasClosedShareTutorial}
      title={!hasClosedShareTutorial ? 'Store Sharing Tips' : ''}
      footer={null}
    >
      <div className="ShareModal-content">
        {isLoading && (
          <div className="spinnerContainer">
            <Spinner size="large" />
          </div>
        )}
        {!hasClosedShareTutorial && !isLoading && (
          <div className="howToShareContainer">
            <ol>
              <li>
                Customize your message to include WHAT you are selling. Tell guests what&apos;s
                available!
              </li>
              <li>See example messages under &quot;Message Templates&quot; on the next page.</li>
              <li>
                Share in your automated messages:
                <ul>
                  <li>after booking</li>
                  <li>one week before arrival</li>
                  <li>at check in</li>
                </ul>
              </li>
            </ol>

            <div className="howToButtonContainer">
              <Button variant="contained" onClick={handleCloseShareTutorial}>
                Let&apos;s go!
              </Button>
            </div>
          </div>
        )}
        {hasClosedShareTutorial && !isLoading && isOpen && (
          <>
            <Tabs activeKey={activeTab} onChange={(val) => setActiveTab(val)} centered>
              <Tabs.TabPane tab="Share Store" key="store-link">
                <StoreLinkContent
                  storeLink={storeLink}
                  storeId={storeId}
                  storeHasSharedStore={hasSharedStore}
                />
                <MessageContent
                  storeLink={storeLink}
                  storeId={storeId}
                  storeHasSharedStore={hasSharedStore}
                />
                <LogoLinks />
              </Tabs.TabPane>
              <Tabs.TabPane tab="Embed Store" key="iframe">
                <StoreIframeContent
                  storeIframe={storeIframe}
                  storeId={storeId}
                  storeHasSharedStore={hasSharedStore}
                />
              </Tabs.TabPane>
            </Tabs>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ShareModal;
